"use client";

import * as React from "react";
import { TriangleAlert as AlertTriangle, CircleCheck as CheckCircle2, Clock, FileText, Flame, CircleQuestionMark as HelpCircle, PhoneCall, ShieldCheck, Sparkles, Wrench, Zap } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
const productLines = [{
  id: "tankless-sensei",
  title: "Rinnai SENSEI Series (RX/RUR)",
  badge: "High-Efficiency Condensing",
  description: "Ultra-high efficiency condensing tankless water heaters offering up to 0.98 UEF with built-in recirculation technology.",
  features: ["Circ-Logic recirculation for instant hot water", "Commercial-grade primary & secondary heat exchangers", "Wi-Fi monitoring compatible (control-r module)", "Compact indoor & outdoor installation footprint"],
  commonTasks: "Flushing mineral buildup, recirculation pump checks, venting calibration."
}, {
  id: "tankless-non-condensing",
  title: "Rinnai Non-Condensing (V Series / HE+)",
  badge: "Value & Standard Gas",
  description: "Durable, space-saving continuous flow water heaters designed for consistent residential output and retrofit installs.",
  features: ["Continuous hot water delivery up to 9.8 GPM", "Direct vent & outdoor termination options", "Enhanced scale detection protection system", "Self-diagnostic electronic ignition and flame rods"],
  commonTasks: "Igniter cleaning, gas valve testing, water flow sensor servicing."
}, {
  id: "hybrid-electric",
  title: "Rinnai Hybrid & Electric Systems",
  badge: "Heat Pump & Hybrid",
  description: "Advanced energy-saving water heating systems engineered to drastically reduce electricity costs while maintaining hot water volume.",
  features: ["Multiple operating modes (Efficiency, Hybrid, High Demand)", "Zero-emission operation for modern eco builds", "Whisper-quiet compressor and dual upper/lower elements", "Integrated leak detection sensor with auto-shutoff"],
  commonTasks: "Air filter cleaning, element replacement, condensate line clearing."
}];
const errorCodes = [{
  code: "Error Code 11",
  issue: "No Ignition / Flame Failure",
  cause: "Gas valve turned off, low gas supply pressure, dirty igniter rod, or electrical wiring fault.",
  action: "Verify gas line valves are fully open. If the unit still does not spark, our plumbers inspect ignition assemblies and gas manifold pressure."
}, {
  code: "Error Code 10",
  issue: "Air Supply or Exhaust Blockage",
  cause: "Venting termination blocked by debris, insect nests, or condensate blockage in the vent run.",
  action: "Clear external vent termination. If error persists, internal blower fans and venting runs require certified inspection."
}, {
  code: "Error Code LC / LC0-LC9",
  issue: "Scale Buildup Warning",
  cause: "Hard water minerals have built up inside the primary copper heat exchanger.",
  action: "Unit requires a full chemical descaling flush with food-grade solution to restore heating efficiency and prevent warranty voiding."
}, {
  code: "Error Code 12",
  issue: "Flame Failure During Operation",
  cause: "Flame rod sensing issues, insufficient gas pressure during high demand, or faulty grounding.",
  action: "Clean flame sensors and test operating manifold pressures under full faucet load."
}];
const warrantyChecklist = [{
  title: "Heat Exchanger Coverage",
  details: "Residential Rinnai tankless heat exchangers generally carry a 12 to 15-year limited manufacturer warranty when properly maintained and registered."
}, {
  title: "Parts & Electronics",
  details: "Original components including PC boards, sensors, and water valves carry a 5-year limited manufacturer replacement warranty."
}, {
  title: "Certified Professional Labor",
  details: "Jonesboro Emergency Plumber provides full diagnostic documentation and labor warranties matching factory standards for warranty repairs."
}, {
  title: "Annual Descaling Records",
  details: "Rinnai requires proof of routine maintenance in hard water zones to maintain warranty claims. We supply official maintenance logs."
}];
export default function RinnaiServices() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="rinnaiwaterheaterservices" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 border-b border-border transition-colors">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-14 sm:mb-18">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider mb-4">
            <Sparkles className="size-3.5 text-accent" />
            <span>Authorized Service & Repair Experts</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground">
            Rinnai Water Heater Diagnostics, Maintenance & Installation
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Fast, factory-grade troubleshooting and certified plumbing repairs for all Rinnai tankless, hybrid, and continuous flow water heating units in Jonesboro, GA and nearby communities.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Badge variant="outline" className="text-xs px-3 py-1">
              <CheckCircle2 className="size-3.5 text-chart-4 mr-1" /> Factory Error Code Diagnostics
            </Badge>
            <Badge variant="outline" className="text-xs px-3 py-1">
              <CheckCircle2 className="size-3.5 text-chart-4 mr-1" /> OEM Rinnai Replacement Parts
            </Badge>
            <Badge variant="outline" className="text-xs px-3 py-1">
              <CheckCircle2 className="size-3.5 text-chart-4 mr-1" /> 24/7 Emergency Dispatch
            </Badge>
          </div>
        </div>

        {/* Urgent Service Alert */}
        <Alert className="mb-12 border-accent/40 bg-card shadow-xs">
          <AlertTriangle className="size-5 text-accent" />
          <AlertTitle className="text-base font-semibold text-card-foreground">
            Is your Rinnai displaying an error code or blowing cold water?
          </AlertTitle>
          <AlertDescription className="text-sm mt-1 text-muted-foreground flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <span>
              Turn off electrical power to the unit if you suspect gas odors or major leaks, then call our Jonesboro emergency repair crew immediately.
            </span>
            <div className="shrink-0">
              <Button asChild size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
                <Link to="/contact/">
                  <PhoneCall className="size-3.5 mr-1" /> Request Fast Dispatch
                </Link>
              </Button>
            </div>
          </AlertDescription>
        </Alert>

        {/* Product Lines Serviced Tabs */}
        <div className="mb-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
            <div>
              <h3 className="text-2xl sm:text-3xl font-default font-bold text-foreground">
                Rinnai Systems & Product Lines We Service
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground mt-1">
                Select your unit category to review key features and common service procedures.
              </p>
            </div>
            <Button asChild variant="outline" size="sm" className="w-fit">
              <Link to="/tankless-water-heater-service/">
                Explore Tankless Solutions
              </Link>
            </Button>
          </div>

          <Tabs defaultValue="tankless-sensei" className="w-full">
            <TabsList className="grid w-full grid-cols-1 sm:grid-cols-3 h-auto p-1 gap-1 mb-6">
              {productLines.map((line, index) => <TabsTrigger key={line.id} value={line.id} data-index={index} className="py-2.5 px-3 text-xs sm:text-sm font-semibold rounded-md transition-all text-center">
                  {line.title.split("(")[0]}
                </TabsTrigger>)}
            </TabsList>

            {productLines.map((line, index) => <TabsContent key={line.id} value={line.id} data-index={index} className="outline-none">
                <motion.div initial={shouldReduceMotion ? {
              opacity: 1
            } : {
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.25
            }}>
                  <Card className="border border-border bg-card shadow-sm">
                    <CardHeader className="pb-4">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <Badge variant="secondary" className="font-semibold text-xs text-secondary-foreground">
                          {line.badge}
                        </Badge>
                        <span className="text-xs text-muted-foreground font-mono">
                          Certified Rinnai Standard Service
                        </span>
                      </div>
                      <CardTitle className="text-xl sm:text-2xl mt-2 font-default">
                        {line.title}
                      </CardTitle>
                      <CardDescription className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                        {line.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6 pt-0">
                      <div>
                        <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">
                          Key Equipment Capabilities
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {line.features.map((feature, fIndex) => <div key={fIndex} data-index={fIndex} className="flex items-start gap-2 text-sm text-foreground">
                              <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                              <span>{feature}</span>
                            </div>)}
                        </div>
                      </div>

                      <div className="rounded-lg bg-muted/60 p-4 border border-border/80">
                        <div className="flex items-start gap-3">
                          <Wrench className="size-4 text-primary shrink-0 mt-0.5" />
                          <div>
                            <span className="text-xs font-bold uppercase tracking-wider text-foreground block mb-0.5">
                              Routine Service & Repair Focus
                            </span>
                            <p className="text-xs sm:text-sm text-muted-foreground">
                              {line.commonTasks}
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              </TabsContent>)}
          </Tabs>
        </div>

        {/* Error Codes & Troubleshooting Guide */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 items-start">
          <div className="lg:col-span-5 space-y-4">
            <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary">
              <Zap className="size-3.5 text-accent" />
              <span>Diagnostic Center</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-default font-bold text-foreground">
              Common Rinnai Error Codes & Troubleshooting
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Rinnai digital controllers present two-digit codes to pinpoint operational locks. Review standard diagnostics below before calling our service team.
            </p>

            <div className="rounded-xl border border-border bg-card p-5 space-y-4 shadow-2xs">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10 text-primary">
                  <Flame className="size-5" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground">Hard Water Descaling Needed?</h4>
                  <p className="text-xs text-muted-foreground">Georgia minerals cause code LC over time.</p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                We perform pressurized lime and mineral removal flushes in under 60 minutes to restore maximum heating performance.
              </p>
              <Button asChild size="sm" variant="secondary" className="w-full">
                <Link to="/water-heater-repair/">
                  Schedule Descaling Service
                </Link>
              </Button>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {errorCodes.map((item, index) => <Card key={item.code} data-index={index} className="border border-border bg-card hover:border-primary/40 transition-colors shadow-2xs">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-xs font-bold text-destructive bg-destructive/10 px-2 py-0.5 rounded">
                        {item.code}
                      </span>
                      <span className="text-[11px] text-muted-foreground">Status Lock</span>
                    </div>
                    <CardTitle className="text-base font-semibold mt-2 text-foreground font-default">
                      {item.issue}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-xs space-y-2 pt-0">
                    <p className="text-muted-foreground">
                      <strong className="text-foreground font-medium">Likely Cause: </strong>
                      {item.cause}
                    </p>
                    <p className="text-muted-foreground bg-muted/40 p-2 rounded border border-border/50">
                      <strong className="text-foreground font-medium">Resolution: </strong>
                      {item.action}
                    </p>
                  </CardContent>
                </Card>)}
            </div>
          </div>
        </div>

        {/* Warranty & Coverage Information */}
        <div className="rounded-2xl border border-border bg-muted/30 p-6 sm:p-10 mb-16">
          <div className="max-w-2xl mb-8">
            <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary mb-2">
              <ShieldCheck className="size-3.5 text-chart-4" />
              <span>Manufacturer Protection</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-default font-bold text-foreground">
              Rinnai Warranty Support & Service Coverage
            </h3>
            <p className="text-sm text-muted-foreground mt-2">
              We help local Jonesboro homeowners verify warranty status, submit claims for defective parts, and execute authorized repairs without jeopardizing factory guarantees.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {warrantyChecklist.map((item, index) => <div key={item.title} data-index={index} className="p-5 rounded-xl bg-background border border-border shadow-2xs flex flex-col justify-between">
                <div>
                  <div className="size-8 rounded-lg bg-secondary flex items-center justify-center text-secondary-foreground mb-3">
                    <FileText className="size-4 text-primary" />
                  </div>
                  <h4 className="text-sm font-bold text-foreground mb-1.5 font-default">
                    {item.title}
                  </h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {item.details}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {/* Troubleshooting FAQs */}
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <h3 className="text-2xl sm:text-3xl font-default font-bold text-foreground">
              Frequently Asked Questions About Rinnai Units
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Quick answers to everyday questions about Rinnai water heater operation.
            </p>
          </div>

          <Accordion type="single" collapsible className="w-full bg-card rounded-xl border border-border px-4 sm:px-6 shadow-xs">
            <AccordionItem value="faq-1" data-index={0}>
              <AccordionTrigger className="text-left font-medium text-sm sm:text-base text-foreground">
                How often does a Rinnai tankless water heater need maintenance?
              </AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                Rinnai recommends flushing the heat exchanger at least once every 12 months with a specialized non-toxic descaling solution to dissolve calcium and scale. Homes with hard municipal water or heavy daily usage may benefit from bi-annual maintenance.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-2" data-index={1}>
              <AccordionTrigger className="text-left font-medium text-sm sm:text-base text-foreground">
                Why does my Rinnai produce hot water for a minute, then go cold?
              </AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                This is frequently caused by a minimum flow rate issue (under 0.4 GPM), a dirty intake cold water filter, low gas pressure when the burner modulates up, or a clogged vent system causing safety flame shutoffs.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-3" data-index={2}>
              <AccordionTrigger className="text-left font-medium text-sm sm:text-base text-foreground">
                Can you replace my old tank water heater with a new Rinnai tankless system?
              </AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                Yes. Our plumbers handle full conversion installations including new dedicated gas supply lines, PVC/polypropylene venting paths, condensation drains, and electrical supply.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-4" data-index={3}>
              <AccordionTrigger className="text-left font-medium text-sm sm:text-base text-foreground">
                How quickly can you dispatch a plumber to Jonesboro for an emergency repair?
              </AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                Our technicians are stationed locally in Jonesboro, GA and offer 24/7 emergency dispatch. Typical response times are within 45 to 90 minutes for urgent leaks or complete loss of hot water.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        {/* Action Bottom Bar */}
        <div className="mt-14 pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
          <div>
            <h4 className="text-base font-bold text-foreground">Ready for fast Rinnai water heater service?</h4>
            <p className="text-xs sm:text-sm text-muted-foreground">Certified technicians available for same-day troubleshooting and replacement.</p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Button asChild variant="outline">
              <Link to="/water-heater-replacement/">
                Replacement Quotes
              </Link>
            </Button>
            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/contact/">
                Contact Our Team
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}